import { forwardRef, useId, useState, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";
import { Icon } from "@/components/layout/Icon";

interface FieldWrapperProps {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Opt-in success indication (green border/ring + checkmark) once a field
   * has been positively validated - e.g. an email that now matches its zod
   * pattern. A new, disclosed capability, not wired into every form; a
   * caller decides when a field's validity is worth confirming rather than
   * just leaving it neutral. Ignored while `error` is set - a field can't
   * be both invalid and valid at once, error wins. */
  valid?: boolean;
}

function FieldChrome({
  label,
  error,
  hint,
  required,
  htmlFor,
  children,
}: FieldWrapperProps & { htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

const baseFieldClass =
  "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 " +
  "transition-colors duration-200 ease-out hover:border-slate-400 " +
  "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 " +
  "disabled:bg-slate-50 disabled:text-slate-500";

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FieldWrapperProps {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, required, valid, id, ...props }, ref) => {
    const showValid = valid && !error;
    // A caller almost never passes `id` (react-hook-form's `register()`
    // spreads name/onChange/onBlur/ref, never id) - without a fallback here,
    // FieldChrome's `<label htmlFor={id}>` silently has nothing to point at
    // and the visible label has zero programmatic association with this
    // field for a screen reader. useId() guarantees a stable id exists
    // either way, whether or not the caller supplies one of their own.
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    return (
      <FieldChrome label={label} error={error} hint={hint} required={required} htmlFor={fieldId}>
        <div className="relative">
          <input
            ref={ref}
            id={fieldId}
            className={cn(
              baseFieldClass,
              showValid && "border-emerald-400 pr-9 focus:border-emerald-500 focus:ring-emerald-100",
              error && "border-red-400 focus:border-red-500 focus:ring-red-100",
              className,
            )}
            {...props}
          />
          {showValid && (
            <Icon
              name="check-circle"
              className="pointer-events-none absolute inset-y-0 right-3 my-auto h-4 w-4 text-emerald-500"
              aria-hidden="true"
            />
          )}
        </div>
      </FieldChrome>
    );
  },
);
Input.displayName = "Input";

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type">, FieldWrapperProps {}

/**
 * A password field with a show/hide toggle - the eye icon button sits
 * inside the field on the right, matching every other icon-in-input pattern
 * in this UI kit. Defaults to masked (`type="password"`); toggling never
 * submits an enclosing form (`type="button"`) and is fully keyboard-
 * operable since it's a real `<button>`, not a clickable span.
 *
 * Used everywhere in the app a person types their own password (sign-in,
 * change password, reset password) - see those pages for the exact
 * `{...register("password")}` usage, unchanged from plain `Input`.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, label, error, hint, required, id, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    const toggleLabel = visible ? "Hide password" : "Show password";
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    return (
      <FieldChrome label={label} error={error} hint={hint} required={required} htmlFor={fieldId}>
        <div className="relative">
          <input
            ref={ref}
            id={fieldId}
            type={visible ? "text" : "password"}
            className={cn(
              baseFieldClass,
              "pr-10",
              error && "border-red-400 focus:border-red-500 focus:ring-red-100",
              className,
            )}
            {...props}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            title={toggleLabel}
            aria-label={toggleLabel}
            aria-pressed={visible}
            tabIndex={0}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 transition-colors duration-200 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-inset rounded-r-lg"
          >
            <Icon name={visible ? "eye-off" : "eye"} className="h-4 w-4" />
          </button>
        </div>
      </FieldChrome>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldWrapperProps {}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, hint, required, valid, id, children, ...props }, ref) => {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    return (
    <FieldChrome label={label} error={error} hint={hint} required={required} htmlFor={fieldId}>
      <select
        ref={ref}
        id={fieldId}
        className={cn(
          baseFieldClass,
          "appearance-none bg-no-repeat pr-8",
          // No checkmark icon here (unlike Input) - the chevron background-
          // image already occupies the right-side slot a checkmark would
          // collide with; the color change alone still communicates validity.
          valid && !error && "border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100",
          error && "border-red-400",
          className,
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%2364748b' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E\")",
          backgroundPosition: "right 0.5rem center",
          backgroundSize: "1.25rem",
        }}
        {...props}
      >
        {children}
      </select>
    </FieldChrome>
    );
  },
);
Select.displayName = "Select";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldWrapperProps {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, required, valid, id, ...props }, ref) => {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    return (
    <FieldChrome label={label} error={error} hint={hint} required={required} htmlFor={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        className={cn(
          "min-h-20 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900",
          "transition-colors duration-200 ease-out hover:border-slate-400",
          valid && !error && "border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100",
          "placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100",
          error && "border-red-400",
          className,
        )}
        {...props}
      />
    </FieldChrome>
    );
  },
);
Textarea.displayName = "Textarea";

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => (
    <label htmlFor={id} className="flex items-start gap-2.5 cursor-pointer select-none">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className={cn(
          "mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-700 transition-colors duration-200 focus:ring-2 focus:ring-primary-200",
          className,
        )}
        {...props}
      />
      <span>
        <span className="block text-sm font-medium text-slate-700">{label}</span>
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </span>
    </label>
  ),
);
Checkbox.displayName = "Checkbox";
